"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useWork } from "../../context/WorkContext";
import { useTaskContext } from "../../context/TaskContext";
import { useEmployee } from "../../context/EmployeeContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Briefcase, Percent, Clock, Timer, AlertTriangle, ListChecks, Eye, Loader2, CheckCircle2 } from "lucide-react";
import { CDN_URL } from "../../config/apiConfig";

/* Stat card + status pill color tokens, matching the reference dashboard */
const STAT_CARDS = [
  { key: "total", label: "Total Tasks", bg: "bg-gradient-to-br from-indigo-100 via-indigo-50 to-white", iconBg: "bg-indigo-500", icon: Briefcase },
  { key: "rate", label: "Completion Rate", bg: "bg-gradient-to-br from-amber-100 via-amber-50 to-white", iconBg: "bg-amber-500", icon: Percent },
  { key: "due", label: "Tasks Due Today", bg: "bg-gradient-to-br from-rose-100 via-rose-50 to-white", iconBg: "bg-rose-500", icon: Clock },
  { key: "hours", label: "Hours This Week", bg: "bg-gradient-to-br from-emerald-100 via-emerald-50 to-white", iconBg: "bg-emerald-500", icon: Timer },
];

const STATUS_META = {
  Completed: { pill: "bg-emerald-100 text-emerald-700", iconBg: "bg-emerald-500" },
  "In Progress": { pill: "bg-amber-100 text-amber-700", iconBg: "bg-amber-500" },
  "In Review": { pill: "bg-sky-100 text-sky-700", iconBg: "bg-sky-500" },
  Pending: { pill: "bg-rose-100 text-rose-700", iconBg: "bg-rose-500" },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || { pill: "bg-gray-100 text-gray-600", iconBg: "bg-gray-400" };

const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return "";
  const f = firstName ? firstName[0].toUpperCase() : "";
  const l = lastName ? lastName[0].toUpperCase() : "";
  return f + l;
};

// Handles number vs string id mismatches between the logged-in user object
// and the task's user_id coming from the tasks API.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const EmployeeWork = () => {
  const { updateProgressAPI } = useWork();
  const { tasks: allTasks } = useTaskContext();
  const { getEmployeeById } = useEmployee();

  const [employee, setEmployee] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const employeeId = user ? user.id : null;

  // Same tasks source (TaskContext -> /tasks) used across the rest of the
  // app, filtered down to the logged-in employee's own tasks.
  const tasks = useMemo(
    () => (allTasks || []).filter((t) => sameId(t.user_id, employeeId)),
    [allTasks, employeeId]
  );

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [chartData, setChartData] = useState([]);

  const parseTimeToHours = (timeStr) => {
    if (!timeStr) return 0;

    let total = 0;

    const parts = timeStr.split(",");

    parts.forEach((p) => {
      p = p.trim().toLowerCase();

      if (p.includes("h")) {
        total += parseFloat(p); // "8 hrs" → 8
      } else if (p.includes("min")) {
        total += parseFloat(p) / 60; // "20 min" → 0.33
      } else if (p.includes("d")) {
        total += parseFloat(p) * 8; // 1 day = 8 hours
      } else {
        total += parseFloat(p);
      }
    });

    return total;
  };

  useEffect(() => {
    if (!tasks || tasks.length === 0) {
      setChartData(
        weekDays.map((day) => ({
          day,
          completed: 0,
          overtime: 0,
        }))
      );
      return;
    }

    const dayMap = {};
    weekDays.forEach((day) => {
      dayMap[day] = { completed: 0, overtime: 0 };
    });

    let accumulatedCompletedHours = 0;
    let overtime = 0;

    const getDayName = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr + "T00:00:00+05:30");
      if (isNaN(date.getTime())) return null;
      return new Intl.DateTimeFormat("en-IN", { weekday: "short" }).format(date);
    };

    tasks.forEach((task) => {
      const assignedHours = parseTimeToHours(task.assigned_hours || task.time);
      let totalCompletedHours = 0;

      if (Array.isArray(task.progress_history) && task.progress_history.length > 0) {
        task.progress_history.forEach((progress) => {
          const dayName = getDayName(progress.date);
          const completedHours = parseTimeToHours(progress.completed_hours);

          if (dayMap[dayName]) {
            dayMap[dayName].completed += completedHours;
            totalCompletedHours += completedHours;
          }
        });
      }

      accumulatedCompletedHours += totalCompletedHours;

      if (accumulatedCompletedHours > assignedHours) {
        overtime = accumulatedCompletedHours - assignedHours;
      }

      task.progress_history?.forEach((progress, index) => {
        const dayName = getDayName(progress.date);

        if (dayMap[dayName] && accumulatedCompletedHours > assignedHours) {
          if (index === task.progress_history.length - 1) {
            dayMap[dayName].overtime += overtime;
          }
        }
      });
    });

    const formattedData = weekDays.map((day) => ({
      day,
      completed: parseFloat(dayMap[day].completed.toFixed(2)),
      overtime: parseFloat(dayMap[day].overtime.toFixed(2)),
    }));

    setChartData(formattedData);
  }, [tasks]);

  useEffect(() => {
    if (!employeeId) {
      console.error("Invalid employeeId or user not found in localStorage");
      return;
    }

    let cancelled = false;
    getEmployeeById(employeeId).then((data) => {
      if (!cancelled) setEmployee(data);
    });
    return () => {
      cancelled = true;
    };
  }, [employeeId, getEmployeeById]);

  const [projects, setProjects] = useState([]);

  // Maps a raw task status to its Kanban stage, matching STATUS_TO_COL in
  // components/TasksPages/Kanban board.jsx.
  const STATUS_TO_STAGE = {
    "To Do": "todo",
    Inprogress: "inprogress",
    Deployment: "deployment",
    ReadyForQA: "readyforqa",
    qa: "readyforqa",
    Inreview: "qa",
    "Ready For Production": "review",
    Completed: "done",
    Reopened: "reopened",
    Cancelled: "cancelled",
  };

  // Fixed completion % per Kanban stage, so the Timeline progress bar tracks
  // where a task sits in the pipeline rather than an arbitrary field.
  const STAGE_PROGRESS = {
    todo: 0,
    inprogress: 10,
    deployment: 20,
    readyforqa: 30,
    qa: 40,
    review: 80,
    done: 100,
    reopened: 0,
    cancelled: 0,
  };

  const getTaskProgress = (task) => {
    const stage = STATUS_TO_STAGE[task.status] ?? "todo";
    return STAGE_PROGRESS[stage] ?? 0;
  };

  useEffect(() => {
    if (tasks && tasks.length > 0) {
      const formatted = tasks.map((t) => ({
        id: t.id,
        title: t.title,
        status: t.status,
        start: t.start_date,
        end: t.end_date,
        progress: getTaskProgress(t),
        task: t.description,
      }));

      setProjects(formatted);
    }
  }, [tasks]);

  const getProgress = (done, total) => Math.round((done / total) * 100);
  const [dropdown, setDropdown] = useState(false);

  const updateProgress = async (index, newProgress) => {
    const previousProgress = projects[index].progress;

    setProjects((prev) =>
      prev.map((p, i) => (i === index ? { ...p, progress: newProgress } : p))
    );

    const projectId = projects[index].id;

    const response = await updateProgressAPI(projectId, newProgress);

    if (!response) {
      setProjects((prev) =>
        prev.map((p, i) => (i === index ? { ...p, progress: previousProgress } : p))
      );
    }
  };

  /* ---- derived stats for the top summary cards ---- */
  const totalTasks = tasks?.length || 0;
  const completedCount = tasks?.filter((t) => t.status === "Completed").length || 0;
  const completionRate = totalTasks ? Math.round((completedCount / totalTasks) * 100) : 0;
  const todayStr = new Date().toISOString().slice(0, 10);
  const dueTodayCount = tasks?.filter((t) => t.end_date === todayStr).length || 0;
  const hoursThisWeek = chartData
    .reduce((sum, d) => sum + (d.completed || 0) + (d.overtime || 0), 0)
    .toFixed(1);

  const statValues = { total: totalTasks, rate: `${completionRate}%`, due: dueTodayCount, hours: hoursThisWeek };

  /* ---- Task Status metrics ---- */
  const overdueCount =
    tasks?.filter((t) => t.end_date && t.end_date < todayStr && t.status !== "Completed").length || 0;
  const inReviewCount = tasks?.filter((t) => t.status === "In Review").length || 0;
  const inProgressCount = tasks?.filter((t) => t.status === "In Progress").length || 0;

  const TASK_STATUS_ITEMS = [
    { key: "overdue", label: "Overdue Tasks", value: overdueCount, icon: AlertTriangle, tint: "bg-red-50", iconColor: "text-red-600" },
    { key: "total", label: "Total Tasks", value: totalTasks, icon: ListChecks, tint: "bg-slate-100", iconColor: "text-slate-600" },
    { key: "review", label: "In Review", value: inReviewCount, icon: Eye, tint: "bg-sky-50", iconColor: "text-sky-600" },
    { key: "progress", label: "In Progress", value: inProgressCount, icon: Loader2, tint: "bg-amber-50", iconColor: "text-amber-600" },
    { key: "completed", label: "Completed", value: completedCount, icon: CheckCircle2, tint: "bg-emerald-50", iconColor: "text-emerald-600" },
    { key: "logged", label: "Logged Hours", value: hoursThisWeek, icon: Timer, tint: "bg-blue-50", iconColor: "text-blue-600" },
  ];

  return (
    <div className="bg-sky-50 min-h-screen p-8 max-sm:p-4">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Employee Works</h1>

        {employee && (
          <div className="flex items-center gap-3">
            <img
              src={`${CDN_URL}/${employee?.image}`}
              alt="Employee"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                {employee.first_name} {employee.last_name}
              </p>
              <p className="text-xs text-gray-500 leading-tight">Software Developer</p>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {STAT_CARDS.map(({ key, label, bg, iconBg, icon: Icon }) => (
          <div key={key} className={`${bg} rounded-2xl p-4 sm:p-5 flex flex-col gap-3 shadow-sm`}>
            <div className={`${iconBg} w-9 h-9 rounded-full flex items-center justify-center`}>
              <Icon className="text-white" size={18} />
            </div>
            <div>
              <p className="text-gray-600 text-xs sm:text-sm">{label}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-800">{statValues[key]}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Weekly Workload */}
      <div className="bg-white p-5 rounded-2xl shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Weekly Workload</h2>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barCategoryGap={20}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
            <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={28} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
            <Bar dataKey="completed" fill="#6366F1" name="Completed Hours" radius={[6, 6, 0, 0]} />
            <Bar dataKey="overtime" fill="#F59E0B" name="Overtime Hours" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Task Status */}
      <div className="bg-white p-5 rounded-2xl shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Task Status</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {TASK_STATUS_ITEMS.map(({ key, label, value, icon: Icon, tint, iconColor }) => (
            <div
              key={key}
              className="rounded-xl border border-gray-100 p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow"
            >
              <div className={`${tint} w-10 h-10 rounded-xl flex items-center justify-center`}>
                <Icon className={iconColor} size={18} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 leading-none">{value}</p>
                <p className="text-xs text-gray-500 mt-1.5">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline / Task table */}
      <div className="bg-white p-5 rounded-2xl shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Timeline / Task Board</h2>
        </div>

        {tasks && tasks.length > 0 ? (
          <>
            {/* table header - desktop only */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-3 pb-2 text-xs font-medium text-gray-400 uppercase tracking-wide border-b">
              <span className="col-span-4">Task</span>
              <span className="col-span-2">Start Date</span>
              <span className="col-span-2">End Date</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-2">Progress</span>
            </div>

            <div className="divide-y">
              {tasks.map((task, index) => {
                const meta = getStatusMeta(task.status);
                return (
                  <div
                    key={task.id || index}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center px-3 py-4"
                  >
                    <div className="md:col-span-4 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{task.title}</p>
                      <p className="text-xs text-gray-400 truncate">{task.description}</p>
                    </div>

                    <div className="md:col-span-2 text-sm text-gray-600">
                      <span className="md:hidden text-gray-400">Start: </span>
                      {task.start_date || "—"}
                    </div>

                    <div className="md:col-span-2 text-sm text-gray-600">
                      <span className="md:hidden text-gray-400">End: </span>
                      {task.end_date || "—"}
                    </div>

                    <div className="md:col-span-2">
                      <span className={`${meta.pill} text-xs font-medium px-3 py-1 rounded-full inline-block`}>
                        {task.status || "Pending"}
                      </span>
                    </div>

                    <div className="md:col-span-2 flex items-center gap-2">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={projects[index]?.progress ?? 0}
                        onChange={async (e) => {
                          const newProgress = Math.min(100, Math.max(0, Number(e.target.value)));
                          await updateProgress(index, newProgress);
                        }}
                        className="w-full accent-indigo-500"
                      />
                      <span className="text-xs font-semibold text-gray-600 w-9 text-right">
                        {projects[index]?.progress ?? 0}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-gray-500 text-sm py-6 text-center">No tasks found.</p>
        )}
      </div>
    </div>
  );
};

export default EmployeeWork;